import React from 'react'
import { Loader2, Undo2, Ban, Eye } from 'lucide-react'
import { Tooltip } from '../ui'
import { BrandAppearance, RefZone } from '../../services/broadcastAdminService'
import { secs, pct, conf, areaSec, REASONS } from './broadcastReviewConstants'

interface Props {
  rows: BrandAppearance[]
  active: BrandAppearance | null
  zones: RefZone[]
  onSelect: (row: BrandAppearance) => void
  /** Omitted entirely for a sponsor, and on read-only surfaces. Its absence
   *  is what removes the column, so the header and rows cannot disagree. */
  onToggleExclude?: (id: string, restore: boolean) => void
  excludingId?: string | null
  /** A drawer wants a short list under the frame; a page wants it to fill
   *  the column beside one. */
  maxHeightClass?: string
}

/**
 * Every appearance of one brand, as a selectable list.
 *
 * SHARED BY THE DRAWER AND THE BRAND PAGE, which is the reason it is its own
 * file. The two differ only in how tall the list may grow and whether the
 * exclude control is offered. Everything else — column widths, the
 * corrected-zone dot, the ordering — has to stay identical, or the same
 * brand reads differently depending on which surface you opened it from.
 *
 * Ordered by screen area rather than time, so the clearest look comes first:
 * the top row is the one worth checking the frame against, and a
 * chronological list buries it.
 */
const BrandAppearanceTable: React.FC<Props> = ({
  rows, active, zones, onSelect, onToggleExclude, excludingId = null,
  maxHeightClass = 'max-h-72'
}) => {
  const canExclude = !!onToggleExclude

  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
        All appearances
      </h4>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Headers: four unlabelled numeric columns were unreadable
            without counting across to the frame to work out which was
            which. Sticky so they survive the scroll. */}
        {/* MIRRORS THE ROW BELOW, which is a wrapper holding a flex-1
            button plus the exclude control OUTSIDE it. A single flat
            header drifted ~28px out of true against every column once
            that control appeared. The trailing spacer is that control's
            width (px-2 + a 12px icon) and only exists when it does. */}
        <div className="flex items-center bg-gray-50 border-b border-gray-200 border-l-2 border-l-transparent text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
          <div className="flex-1 min-w-0 flex items-center gap-3 pr-1 pl-2.5 py-1.5 whitespace-nowrap">
            <span className="w-12">At</span>
            {/* Location gives up the room. It is the only elastic column
                and the only one that can truncate without losing a
                figure — a clipped zone name is still recognisable, a
                clipped number is wrong. */}
            <span className="flex-1 min-w-0">Placement</span>
            <span className="w-14 text-right">On screen</span>
            <span className="w-12 text-right">Area-sec</span>
            <span className="w-12 text-right">Max size</span>
            <span className="w-12 text-right">Quality</span>
            <span className="w-5" />
          </div>
          {canExclude && (
            <span className="w-7 flex-shrink-0" aria-hidden="true" />
          )}
        </div>
        <div className={`divide-y divide-gray-100 ${maxHeightClass} overflow-y-auto`}>
        {rows.map((r) => {
          const on = active?.id === r.id
          const gone = !!r.excluded_reason
          return (
            <div key={r.id} className={`flex items-stretch border-l-2 transition-colors ${
              on ? 'bg-blue-50 border-blue-600'
                 : 'bg-transparent border-transparent hover:bg-gray-50'
            }`}>
            <button
              type="button"
              onClick={() => onSelect(r)}
              aria-current={on}
              className={`flex-1 min-w-0 flex items-center gap-3 pr-1 pl-2.5 py-1.5 text-left text-[11px] ${
                gone ? 'line-through opacity-50' : ''
              }`}
            >
              <span className={`w-12 tabular-nums font-medium ${on ? 'text-blue-800' : 'text-gray-900'}`}>
                {secs(r.peak_frame_ms ?? r.start_time_ms)}
              </span>
              <span className={`flex-1 min-w-0 truncate ${on ? 'text-blue-700' : 'text-gray-600'}`}>
                {zones.find((z) => z.code === r.zone_code)?.label ?? r.zone_code}
                {r.zone_code_original && r.zone_code_original !== r.zone_code && (
                  <Tooltip content="Placement corrected by a reviewer" placement="top">
                    <span className="ml-1 text-amber-600 cursor-help">•</span>
                  </Tooltip>
                )}
              </span>
              <span className={`w-14 text-right tabular-nums ${on ? 'text-blue-700' : 'text-gray-600'}`}>
                {secs(r.duration_ms)}
              </span>
              {/* AREA-SECONDS IS THE ONE THAT DECIDES MONEY, so it is
                  weighted like the figure it is rather than hidden
                  among the diagnostics. Duration and max size each
                  explain half of it: a board can be on screen a long
                  time and still be worth little. */}
              <span className={`w-12 text-right tabular-nums font-medium ${on ? 'text-blue-800' : 'text-gray-900'}`}>
                {areaSec(r.area_seconds)}
              </span>
              <span className={`w-12 text-right tabular-nums ${on ? 'text-blue-700' : 'text-gray-600'}`}>
                {pct(r.max_area_share)}
              </span>
              {/* Averaged over this appearance's frames, not per frame.
                  Per-frame confidence exists in the detections table and
                  is not surfaced — at 1 fps a three-hour game is ~10,000
                  rows per brand, which is a different screen. */}
              <span className={`w-12 text-right tabular-nums ${on ? 'text-blue-700' : 'text-gray-400'}`}>
                {conf(r.avg_confidence)}
              </span>
              <span className="w-5 flex justify-end">
                {on && <Eye className="h-3 w-3 text-blue-600" />}
              </span>
            </button>
            {/* Per-occurrence, because a model that reads a real sponsor
                onto the wrong frame should cost that frame and not the
                sponsor. Support only — the customer source omits it. */}
            {canExclude && (
              <Tooltip
                placement="left"
                maxWidth={240}
                // Tooltip's wrapper defaults to inline-block, which does
                // not stretch in an items-stretch flex row — the button
                // floated above the row's centre line.
                wrapperClassName="flex"
                content={gone
                  ? `Excluded as ${REASONS.find((x) => x.value === r.excluded_reason)?.label ?? r.excluded_reason}. Restore it so it counts again`
                  : 'Exclude just this occurrence — the brand keeps its other appearances'}
              >
                <button
                  type="button"
                  aria-label={gone ? 'Restore this occurrence' : 'Exclude this occurrence'}
                  disabled={excludingId === r.id}
                  onClick={() => onToggleExclude!(r.id, gone)}
                  className="px-2 flex items-center text-gray-300 hover:text-red-600 disabled:opacity-40 disabled:hover:text-gray-300 disabled:cursor-not-allowed"
                >
                  {excludingId === r.id
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : gone ? <Undo2 className="h-3 w-3" /> : <Ban className="h-3 w-3" />}
                </button>
              </Tooltip>
            )}
            </div>
          )
        })}
        {rows.length === 0 && (
          <p className="px-3 py-4 text-xs text-gray-400 text-center">No appearances recorded</p>
        )}
        </div>
      </div>
      <p className="text-[11px] text-gray-400 mt-1.5">
        Ordered by screen area, not time — the clearest look comes first.
        A dot marks a placement a reviewer corrected.
      </p>
    </div>
  )
}

export default BrandAppearanceTable
